import { AxiosInstance, AxiosResponse } from 'axios';

export interface SimulationCubeFilters {
  company_entity_ids?: (string | number)[];
  counterparty_ids?: (string | number)[];
  currency_id?: string | number;
  trade_ids?: (string | number)[];
  classification_tag_ids?: (string | number)[];
  start_date?: string;
  parameters?: SimulationCubeParameters;
  output_currency_id?: string | number | null;
}

export interface SimulationCubeParameters {
  num_simulations?: number;
}

export type CashConsumptionSimulationStatus =
  | 'completed'
  | 'not_started'
  | 'valuation_not_started'
  | 'in_progress'
  | 'failed'
  | 'can_start'
  | 'confirmation_required'
  | 'started'
  | 'already_running'
  | 'already_completed'
  | 'blocked_by_limits';

export interface SimulationRunStatusPayload {
  status: CashConsumptionSimulationStatus;
  message?: string;
  cash_consumption_scenario_set_id?: number;
  cash_consumption_simulation_run_id?: number;
  scenario_definition_hash?: string;
  requested_scenario_definition_hash?: string;
  scenario_set_status?: string;
  trade_id_list?: number[];
  trade_count?: number;
  scenario_date_count?: number;
  estimated_task_count?: number;
  task_count?: number;
  completed_task_count?: number;
  failed_task_count?: number;
  total_task_count?: number;
  progress?: number;
  number_of_simulations?: number;
  trade_batch_size?: number;
  curve_date?: string;
  end_date?: string;
  factor_universe?: string[];
  requires_confirmation?: boolean;
  confirmation_reason?: string;
  can_start?: boolean;
  messages_sent?: number;
  messages_failed?: number;
}

export interface SimulationCubePayload {
  status: CashConsumptionSimulationStatus;
  cash_consumption_scenario_set_id?: number;
  cash_consumption_simulation_run_id?: number;
  scenario_definition_hash?: string;
  progress?: number;
  completed_task_count?: number;
  failed_task_count?: number;
  total_task_count?: number;
  message?: string;
  metadata?: {
    num_simulations: number;
    num_trades: number;
    num_dates: number;
    num_currency_pairs: number;
    curve_date: string;
    currency_id: number;
    storage_currency_id?: number;
    output_currency_id?: number;
    currency_conversion?: {
      applied: boolean;
      from_currency_id: number;
      to_currency_id: number;
      rate_date: string | null;
      spot_rate: string | null;
    };
    warnings?: CurrencyResolutionWarning[];
    created_at: string;
  };
  trade_id_list: number[];
  simulation_data?: Array<{
    date: string;
    simulations: number[][];
  }>;
  spot_rate_cube?: Record<string, Record<string, number[] | null>>;
}

export interface SimulationCubeResponse {
  success: boolean;
  data: SimulationCubePayload;
  error_msg?: string;
}

export interface SimulationRunEstimateResponse {
  success: boolean;
  data: SimulationRunStatusPayload;
  error_msg?: string;
}

export interface SimulationRunStartRequest extends SimulationCubeFilters {
  confirm?: boolean;
}

export interface SimulationRunStartResponse {
  success: boolean;
  data: SimulationRunStatusPayload;
  error_msg?: string;
}

export interface CurrencyResolutionWarning {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface SimulationOutputCurrencyResolutionPayload {
  output_currency_id: number;
  warnings: CurrencyResolutionWarning[];
}

export interface SimulationOutputCurrencyResolutionResponse {
  success: boolean;
  data: SimulationOutputCurrencyResolutionPayload;
  error_msg?: string;
}

export interface SimulationCubeDownloadResult {
  file_name: string;
  file_path?: string;
  file_s3_url: string;
  missing_market_data?: Record<string, unknown>;
}

export interface SimulationCubeDownloadResponse {
  success: boolean;
  data: SimulationCubeDownloadResult;
  error_msg?: string;
}

export default class SimulationAPI {
  private client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  async fetchCube(
    filters: SimulationCubeFilters,
  ): Promise<AxiosResponse<SimulationCubeResponse>> {
    return this.client.post(
      '/v2/simulation/cube/',
      this._buildCubeRequest(filters),
    );
  }

  async downloadCube(
    filters: SimulationCubeFilters,
  ): Promise<AxiosResponse<SimulationCubeDownloadResponse>> {
    return this.client.post(
      '/v2/simulation/cube/download/',
      this._buildCubeRequest(filters),
    );
  }

  async resolveOutputCurrency(
    filters: SimulationCubeFilters,
  ): Promise<AxiosResponse<SimulationOutputCurrencyResolutionResponse>> {
    return this.client.post('/v2/simulation/output-currency/resolve/', {
      filters: this._buildRequestFilters(filters),
    });
  }

  async estimateRun(
    filters: SimulationCubeFilters,
  ): Promise<AxiosResponse<SimulationRunEstimateResponse>> {
    return this.client.post(
      '/v2/simulation/run/estimate/',
      this._buildRunRequest(filters),
    );
  }

  async startRun(
    filters: SimulationRunStartRequest,
  ): Promise<AxiosResponse<SimulationRunStartResponse>> {
    const { confirm, ...cubeFilters } = filters;
    return this.client.post('/v2/simulation/run/', {
      ...this._buildRunRequest(cubeFilters),
      confirm: confirm ?? false,
    });
  }

  private _buildCubeRequest(filters: SimulationCubeFilters) {
    const { start_date, parameters, output_currency_id, ...filterParams } =
      filters;
    return {
      filters: filterParams,
      ...(start_date ? { start_date } : {}),
      ...(parameters ? { parameters } : {}),
      ...(output_currency_id != null ? { output_currency_id } : {}),
    };
  }

  private _buildRunRequest(filters: SimulationCubeFilters) {
    const { start_date, parameters, output_currency_id, ...filterParams } =
      filters;
    return {
      filters: filterParams,
      ...(start_date ? { start_date } : {}),
      ...(parameters ? { parameters } : {}),
    };
  }

  private _buildRequestFilters(filters: SimulationCubeFilters) {
    const { start_date, parameters, output_currency_id, ...filterParams } =
      filters;
    return filterParams;
  }
}
